import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { 
  Zap, Lightbulb, Battery, ArrowLeft, RotateCcw, 
  CheckCircle2, Play, Pause, Info, Star, Volume2,
  Cable, CircleDot, Plus, Minus, HelpCircle, Leaf, Rocket, Cog
} from 'lucide-react';

const CircuitLabPage = () => {
  const navigate = useNavigate();
  const [currentExperiment, setCurrentExperiment] = useState('series');
  const [circuitComplete, setCircuitComplete] = useState(false);
  const [bulbOn, setBulbOn] = useState(false);
  const [voltage, setVoltage] = useState(5);
  const [resistance, setResistance] = useState(100);
  const [showHint, setShowHint] = useState(false);
  const [score, setScore] = useState(0);
  const [switches, setSwitches] = useState({ s1: false, s2: false });
  
  // Science modules for navigation
  const scienceModules = [
    { id: 'circuits', title: 'Circuit Lab', icon: Zap, route: '/science/circuits', color: '#f59e0b' },
    { id: 'biology', title: 'Biology Lab', icon: Leaf, route: '/science/biology', color: '#10b981' },
    { id: 'physics', title: 'Force & Motion', icon: Rocket, route: '/science/physics', color: '#06b6d4' },
    { id: 'machines', title: 'Simple Machines', icon: Cog, route: '/science/machines', color: '#a855f7' }
  ];
  
  // For drag and drop
  const [components, setComponents] = useState([]);
  const [placedComponents, setPlacedComponents] = useState({});
  const dragRef = useRef(null);

  // Calculate current using Ohm's Law
  const current = (voltage / resistance).toFixed(3);
  const power = (voltage * current).toFixed(2);

  // Series circuit logic
  useEffect(() => {
    if (currentExperiment === 'series') {
      setBulbOn(switches.s1);
    } else if (currentExperiment === 'parallel') {
      setBulbOn(switches.s1 || switches.s2);
    } else if (currentExperiment === 'ohm') {
      setBulbOn(voltage > 0 && resistance > 0);
    }
  }, [switches, currentExperiment, voltage, resistance]);

  const experiments = [
    {
      id: 'series',
      title: 'Series Circuit',
      titleHindi: 'श्रेणी परिपथ',
      description: 'Ek switch se bulb control karo!',
      icon: Lightbulb
    },
    {
      id: 'parallel',
      title: 'Parallel Circuit',
      titleHindi: 'समानांतर परिपथ',
      description: 'Do switches, ek bulb - koi bhi ON karo!',
      icon: Cable
    },
    {
      id: 'ohm',
      title: "Ohm's Law",
      titleHindi: 'ओम का नियम',
      description: 'V = I × R - Voltage, Current, Resistance ka relation',
      icon: Zap
    }
  ];

  const toggleSwitch = (switchId) => {
    setSwitches(prev => ({ ...prev, [switchId]: !prev[switchId] }));
    if (!switches[switchId]) {
      setScore(prev => prev + 10);
    }
  };

  const resetExperiment = () => {
    setSwitches({ s1: false, s2: false });
    setVoltage(5);
    setResistance(100);
    setBulbOn(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header with Navigation */}
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/science')}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-white/20"></div>
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                <span className="text-lg font-bold text-white">Circuit Lab</span>
              </div>
            </div>
            
            {/* Module Navigation Tabs */}
            <div className="hidden lg:flex items-center gap-1 bg-white/5 rounded-full p-1 border border-white/10">
              {scienceModules.map((mod) => (
                <button
                  key={mod.id}
                  onClick={() => navigate(mod.route)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                    mod.id === 'circuits' 
                      ? 'bg-white/10 text-yellow-400 font-medium' 
                      : 'text-gray-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <mod.icon className="w-4 h-4" style={{ color: mod.color }} />
                  <span>{mod.title}</span>
                </button>
              ))}
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/20 border border-yellow-500/30 rounded-full">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-300 font-semibold">{score} points</span>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={resetExperiment}
                className="border-white/20 text-white hover:bg-white/10"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar - Experiments */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="bg-black/40 border-white/10 backdrop-blur-xl">
              <CardContent className="p-4">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Play className="w-4 h-4 text-cyan-400" />
                  Experiments
                </h3>
                <div className="space-y-2">
                  {experiments.map((exp) => (
                    <button
                      key={exp.id}
                      onClick={() => {
                        setCurrentExperiment(exp.id);
                        resetExperiment();
                      }}
                      className={`w-full p-3 rounded-xl text-left transition-all ${
                        currentExperiment === exp.id 
                          ? 'bg-gradient-to-r from-cyan-500/30 to-blue-500/30 border border-cyan-500/50' 
                          : 'bg-white/5 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <exp.icon className={`w-5 h-5 ${currentExperiment === exp.id ? 'text-cyan-400' : 'text-white/60'}`} />
                        <div>
                          <p className={`font-medium ${currentExperiment === exp.id ? 'text-white' : 'text-white/80'}`}>
                            {exp.title}
                          </p>
                          <p className="text-xs text-purple-300">{exp.titleHindi}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Hint Card */}
            <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/30 backdrop-blur-xl">
              <CardContent className="p-4">
                <button 
                  onClick={() => setShowHint(!showHint)}
                  className="flex items-center gap-2 text-yellow-300 font-medium mb-2"
                >
                  <HelpCircle className="w-4 h-4" />
                  Hint chahiye?
                </button>
                {showHint && (
                  <div className="text-sm text-yellow-200/80 animate-in slide-in-from-top">
                    {currentExperiment === 'series' && (
                      <p>💡 Series circuit mein ek hi path hota hai. Switch ON karo to current flow hoga!</p>
                    )}
                    {currentExperiment === 'parallel' && (
                      <p>💡 Parallel circuit mein multiple paths hote hain. Koi bhi switch ON karo, bulb jalega!</p>
                    )}
                    {currentExperiment === 'ohm' && (
                      <p>💡 V = I × R. Voltage badhao = Current badhe. Resistance badhao = Current ghate!</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Circuit Area */}
          <div className="lg:col-span-2">
            <Card className="bg-black/40 border-white/10 backdrop-blur-xl h-full">
              <CardContent className="p-6">
                {/* Circuit Visualization */}
                <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 min-h-[400px] border border-white/10">
                  {/* Grid Background */}
                  <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}></div>

                  {currentExperiment === 'series' && (
                    <div className="relative z-10">
                      {/* Series Circuit SVG */}
                      <svg viewBox="0 0 400 300" className="w-full h-auto">
                        {/* Battery */}
                        <g transform="translate(50, 100)">
                          <rect x="0" y="0" width="60" height="100" rx="5" fill="#374151" stroke="#10B981" strokeWidth="2"/>
                          <rect x="20" y="-10" width="20" height="10" fill="#10B981"/>
                          <text x="30" y="50" textAnchor="middle" fill="#10B981" fontSize="12">+</text>
                          <text x="30" y="90" textAnchor="middle" fill="#EF4444" fontSize="12">-</text>
                          <Battery className="w-8 h-8 text-green-400" style={{transform: 'translate(16px, 30px)'}}/>
                        </g>

                        {/* Wires */}
                        <path 
                          d="M 110 120 L 180 120" 
                          stroke={switches.s1 ? "#22D3EE" : "#4B5563"} 
                          strokeWidth="4" 
                          strokeLinecap="round"
                          className="transition-colors duration-300"
                        />
                        <path 
                          d="M 220 120 L 300 120 L 300 180 L 200 180" 
                          stroke={switches.s1 ? "#22D3EE" : "#4B5563"} 
                          strokeWidth="4" 
                          strokeLinecap="round"
                          fill="none"
                          className="transition-colors duration-300"
                        />
                        <path 
                          d="M 200 180 L 80 180 L 80 200 L 80 180 L 80 200" 
                          stroke={switches.s1 ? "#22D3EE" : "#4B5563"} 
                          strokeWidth="4" 
                          strokeLinecap="round"
                          fill="none"
                          className="transition-colors duration-300"
                        />

                        {/* Switch */}
                        <g 
                          transform="translate(180, 100)" 
                          className="cursor-pointer"
                          onClick={() => toggleSwitch('s1')}
                        >
                          <rect x="0" y="0" width="40" height="40" rx="5" fill="#1F2937" stroke="#6B7280" strokeWidth="2"/>
                          <circle cx="20" cy="20" r="12" fill={switches.s1 ? "#22D3EE" : "#374151"} className="transition-colors"/>
                          <text x="20" y="55" textAnchor="middle" fill="#9CA3AF" fontSize="10">Switch</text>
                        </g>

                        {/* Bulb */}
                        <g transform="translate(270, 140)">
                          <circle 
                            cx="30" cy="30" r="30" 
                            fill={bulbOn ? "#FEF08A" : "#374151"} 
                            stroke={bulbOn ? "#FBBF24" : "#6B7280"} 
                            strokeWidth="3"
                            className="transition-all duration-300"
                            style={{
                              filter: bulbOn ? 'drop-shadow(0 0 20px #FEF08A)' : 'none'
                            }}
                          />
                          <path 
                            d="M 20 45 Q 30 55 40 45" 
                            stroke={bulbOn ? "#F59E0B" : "#6B7280"} 
                            strokeWidth="2" 
                            fill="none"
                          />
                          <text x="30" y="80" textAnchor="middle" fill="#9CA3AF" fontSize="10">Bulb</text>
                        </g>

                        {/* Current flow animation */}
                        {bulbOn && (
                          <circle r="4" fill="#22D3EE" className="animate-pulse">
                            <animateMotion dur="2s" repeatCount="indefinite" path="M 110 120 L 180 120 L 220 120 L 300 120 L 300 180 L 80 180 L 80 200" />
                          </circle>
                        )}
                      </svg>

                      {/* Instructions */}
                      <div className="mt-4 text-center">
                        <p className="text-white/80">
                          👆 <span className="text-cyan-300 font-semibold">Switch</span> pe click karo bulb jalane ke liye!
                        </p>
                      </div>
                    </div>
                  )}

                  {currentExperiment === 'parallel' && (
                    <div className="relative z-10">
                      <svg viewBox="0 0 400 350" className="w-full h-auto">
                        {/* Battery */}
                        <g transform="translate(30, 120)">
                          <rect x="0" y="0" width="50" height="80" rx="5" fill="#374151" stroke="#10B981" strokeWidth="2"/>
                          <text x="25" y="35" textAnchor="middle" fill="#10B981" fontSize="14">+</text>
                          <text x="25" y="65" textAnchor="middle" fill="#EF4444" fontSize="14">-</text>
                        </g>

                        {/* Main wires */}
                        <path d="M 80 140 L 120 140" stroke={(switches.s1 || switches.s2) ? "#22D3EE" : "#4B5563"} strokeWidth="4"/>
                        
                        {/* Branch point */}
                        <circle cx="120" cy="140" r="5" fill={(switches.s1 || switches.s2) ? "#22D3EE" : "#4B5563"}/>
                        
                        {/* Upper branch (Switch 1) */}
                        <path d="M 120 140 L 120 100 L 160 100" stroke={switches.s1 ? "#22D3EE" : "#4B5563"} strokeWidth="4" fill="none"/>
                        <g transform="translate(160, 80)" className="cursor-pointer" onClick={() => toggleSwitch('s1')}>
                          <rect x="0" y="0" width="40" height="40" rx="5" fill="#1F2937" stroke="#6B7280" strokeWidth="2"/>
                          <circle cx="20" cy="20" r="10" fill={switches.s1 ? "#22D3EE" : "#374151"}/>
                          <text x="20" y="55" textAnchor="middle" fill="#9CA3AF" fontSize="9">S1</text>
                        </g>
                        <path d="M 200 100 L 280 100" stroke={switches.s1 ? "#22D3EE" : "#4B5563"} strokeWidth="4"/>

                        {/* Lower branch (Switch 2) */}
                        <path d="M 120 140 L 120 180 L 160 180" stroke={switches.s2 ? "#22D3EE" : "#4B5563"} strokeWidth="4" fill="none"/>
                        <g transform="translate(160, 160)" className="cursor-pointer" onClick={() => toggleSwitch('s2')}>
                          <rect x="0" y="0" width="40" height="40" rx="5" fill="#1F2937" stroke="#6B7280" strokeWidth="2"/>
                          <circle cx="20" cy="20" r="10" fill={switches.s2 ? "#22D3EE" : "#374151"}/>
                          <text x="20" y="55" textAnchor="middle" fill="#9CA3AF" fontSize="9">S2</text>
                        </g>
                        <path d="M 200 180 L 280 180" stroke={switches.s2 ? "#22D3EE" : "#4B5563"} strokeWidth="4"/>

                        {/* Join point */}
                        <circle cx="280" cy="140" r="5" fill={(switches.s1 || switches.s2) ? "#22D3EE" : "#4B5563"}/>
                        <path d="M 280 100 L 280 140" stroke={switches.s1 ? "#22D3EE" : "#4B5563"} strokeWidth="4"/>
                        <path d="M 280 180 L 280 140" stroke={switches.s2 ? "#22D3EE" : "#4B5563"} strokeWidth="4"/>

                        {/* To bulb */}
                        <path d="M 280 140 L 320 140" stroke={(switches.s1 || switches.s2) ? "#22D3EE" : "#4B5563"} strokeWidth="4"/>

                        {/* Bulb */}
                        <g transform="translate(320, 110)">
                          <circle 
                            cx="30" cy="30" r="28" 
                            fill={bulbOn ? "#FEF08A" : "#374151"} 
                            stroke={bulbOn ? "#FBBF24" : "#6B7280"} 
                            strokeWidth="3"
                            style={{ filter: bulbOn ? 'drop-shadow(0 0 20px #FEF08A)' : 'none' }}
                          />
                        </g>

                        {/* Return path */}
                        <path d="M 350 170 L 350 250 L 55 250 L 55 200" stroke={(switches.s1 || switches.s2) ? "#22D3EE" : "#4B5563"} strokeWidth="4" fill="none"/>
                      </svg>

                      <div className="mt-4 text-center">
                        <p className="text-white/80">
                          🔀 <span className="text-cyan-300 font-semibold">Parallel Circuit:</span> S1 ya S2, koi bhi ON karo!
                        </p>
                      </div>
                    </div>
                  )}

                  {currentExperiment === 'ohm' && (
                    <div className="relative z-10 space-y-6">
                      {/* Ohm's Law Formula */}
                      <div className="text-center bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-xl p-4 border border-cyan-500/30">
                        <p className="text-2xl font-bold text-white mb-2">
                          V = I × R
                        </p>
                        <p className="text-sm text-cyan-300">
                          Voltage (वोल्टेज) = Current (धारा) × Resistance (प्रतिरोध)
                        </p>
                      </div>

                      {/* Interactive Controls */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white/5 rounded-xl p-4">
                          <label className="text-white/80 text-sm flex items-center gap-2 mb-3">
                            <Battery className="w-4 h-4 text-green-400" />
                            Voltage (V): <span className="text-cyan-400 font-bold">{voltage}V</span>
                          </label>
                          <input 
                            type="range" 
                            min="0" 
                            max="12" 
                            value={voltage}
                            onChange={(e) => setVoltage(Number(e.target.value))}
                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                          />
                        </div>
                        <div className="bg-white/5 rounded-xl p-4">
                          <label className="text-white/80 text-sm flex items-center gap-2 mb-3">
                            <CircleDot className="w-4 h-4 text-orange-400" />
                            Resistance (Ω): <span className="text-orange-400 font-bold">{resistance}Ω</span>
                          </label>
                          <input 
                            type="range" 
                            min="10" 
                            max="500" 
                            step="10"
                            value={resistance}
                            onChange={(e) => setResistance(Number(e.target.value))}
                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
                          />
                        </div>
                      </div>

                      {/* Results */}
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4">
                          <p className="text-green-400 text-xs mb-1">Voltage</p>
                          <p className="text-2xl font-bold text-white">{voltage}<span className="text-sm">V</span></p>
                        </div>
                        <div className="bg-cyan-500/20 border border-cyan-500/30 rounded-xl p-4">
                          <p className="text-cyan-400 text-xs mb-1">Current</p>
                          <p className="text-2xl font-bold text-white">{current}<span className="text-sm">A</span></p>
                        </div>
                        <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-4">
                          <p className="text-yellow-400 text-xs mb-1">Power</p>
                          <p className="text-2xl font-bold text-white">{power}<span className="text-sm">W</span></p>
                        </div>
                      </div>

                      {/* Bulb brightness based on power */}
                      <div className="flex justify-center">
                        <div className="relative">
                          <div 
                            className="w-24 h-24 rounded-full flex items-center justify-center transition-all duration-300"
                            style={{
                              backgroundColor: voltage > 0 ? `rgba(254, 240, 138, ${Math.min(power / 1, 1)})` : '#374151',
                              boxShadow: voltage > 0 ? `0 0 ${power * 30}px rgba(254, 240, 138, ${Math.min(power / 1, 0.8)})` : 'none'
                            }}
                          >
                            <Lightbulb className={`w-12 h-12 ${voltage > 0 ? 'text-yellow-600' : 'text-gray-500'}`} />
                          </div>
                          <p className="text-center text-white/60 text-sm mt-2">
                            Brightness: {Math.round(Math.min(power / 0.5, 1) * 100)}%
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar - Info */}
          <div className="lg:col-span-1 space-y-4">
            {/* Status Card */}
            <Card className={`border-2 ${bulbOn ? 'bg-green-500/10 border-green-500/50' : 'bg-red-500/10 border-red-500/30'} backdrop-blur-xl transition-all`}>
              <CardContent className="p-4 text-center">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3 ${bulbOn ? 'bg-green-500/30' : 'bg-red-500/20'}`}>
                  <Lightbulb className={`w-8 h-8 ${bulbOn ? 'text-green-400' : 'text-red-400'}`} />
                </div>
                <p className={`font-bold text-lg ${bulbOn ? 'text-green-400' : 'text-red-400'}`}>
                  {bulbOn ? '💡 Bulb ON!' : '🔌 Bulb OFF'}
                </p>
                <p className="text-white/60 text-sm mt-1">
                  {bulbOn ? 'बहुत बढ़िया! Circuit complete hai!' : 'Switch ON karo!'}
                </p>
              </CardContent>
            </Card>

            {/* Theory Card */}
            <Card className="bg-black/40 border-white/10 backdrop-blur-xl">
              <CardContent className="p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4 text-cyan-400" />
                  Kya ho raha hai? (Theory)
                </h4>
                <div className="text-sm text-white/70 space-y-2">
                  {currentExperiment === 'series' && (
                    <>
                      <p>📌 <span className="text-cyan-300">Series Circuit</span> mein sab components ek line mein connected hote hain.</p>
                      <p>📌 Agar ek bhi connection toot jaye, circuit break ho jata hai.</p>
                      <p>📌 Current har jagah same rehta hai.</p>
                    </>
                  )}
                  {currentExperiment === 'parallel' && (
                    <>
                      <p>📌 <span className="text-cyan-300">Parallel Circuit</span> mein multiple paths hote hain.</p>
                      <p>📌 Ek path band ho, doosre se current flow hota hai.</p>
                      <p>📌 Ghar ki wiring parallel mein hoti hai!</p>
                    </>
                  )}
                  {currentExperiment === 'ohm' && (
                    <>
                      <p>📌 <span className="text-cyan-300">Ohm's Law:</span> V = I × R</p>
                      <p>📌 Voltage ↑ = Current ↑</p>
                      <p>📌 Resistance ↑ = Current ↓</p>
                      <p>📌 Power = Voltage × Current</p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Achievement */}
            {score >= 30 && (
              <Card className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-yellow-500/50 backdrop-blur-xl animate-in slide-in-from-bottom">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto bg-yellow-500/30 rounded-full flex items-center justify-center mb-2">
                    <Trophy className="w-6 h-6 text-yellow-400" />
                  </div>
                  <p className="text-yellow-300 font-bold">🏆 Badge Unlocked!</p>
                  <p className="text-yellow-200/70 text-sm">Circuit Master</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Trophy icon component
const Trophy = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
    <path d="M4 22h16"/>
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
  </svg>
);

export default CircuitLabPage;
